import api from '@/app/utils/api';
import { getFormData } from '../utils/form';
import {
  CreateFolder,
  CreateVehicleImage,
  WheelImage,
} from '../form/models/CreateFolder';
import { Folder } from '../models/Folder';
import { VehicleDamage } from '../models/VehicleDamage';
import type { SubmissionProgress } from '../form/models/CreateFolder';

// Maps a local vehicle-image/wheel field to the Folder property PATCHed on
// the server.
const VEHICLE_IMAGE_FIELDS: Record<string, string> = {
  front: 'front',
  back: 'back',
  driverSide: 'driverSide',
  passengerSide: 'passengerSide',
  dashboard: 'dashboard',
  interior: 'interior',
};

const WHEEL_FIELDS: Record<string, string> = {
  wheel1: 'wheelFR',
  wheel2: 'wheelFL',
  wheel3: 'wheelRR',
  wheel4: 'wheelRL',
};

export class FolderClient {
  // `onProgress` is called after every unit of work that succeeds, so the
  // caller can checkpoint-save `folder.submissionProgress` — if the request
  // is retried later (offline, app killed mid-submit...), already-completed
  // units are skipped instead of resubmitted, which would otherwise create a
  // duplicate Folder or orphan MediaObjects on the backend.
  public async create(
    folder: CreateFolder,
    onProgress?: () => void
  ): Promise<void> {
    const progress = folder.submissionProgress;

    let content: Folder;
    if (progress.folderId) {
      content = { '@id': progress.folderId } as Folder;
    } else {
      // Only `rest` (the plain FolderCreateDto-shaped fields) is sent — the
      // rest of the destructuring exists purely to exclude these local-only
      // fields from that payload.
      /* eslint-disable @typescript-eslint/no-unused-vars */
      const {
        cg,
        chassis,
        wheels,
        vehicleImages,
        damagesImages,
        infoImages,
        submissionProgress,
        sendPending,
        currentStep,
        ...rest
      } = folder;
      /* eslint-enable @typescript-eslint/no-unused-vars */

      const response = await api('/api/folders', {
        method: 'POST',
        body: JSON.stringify(rest),
      });

      content = await response.json();
      progress.folderId = content['@id'];
      onProgress?.();
    }

    await this.sendFolderVehicleImage(
      folder.vehicleImages,
      folder.wheels,
      content,
      progress,
      onProgress
    );

    if (folder.cg) {
      await this.sendFolderImageOnce(
        'cg',
        folder.cg,
        'cg',
        content,
        progress,
        onProgress
      );
    }

    if (folder.chassis) {
      await this.sendFolderImageOnce(
        'chassis',
        folder.chassis,
        'chassis',
        content,
        progress,
        onProgress
      );
    }

    const damages = folder.damagesImages ?? [];
    // Recorded *before* the loop runs (not after) so that even if this
    // attempt itself fails partway through, the count reflects the shape
    // this attempt actually saw — letting the next retry compare against it.
    this.resetSectionIfShapeChanged(
      progress,
      'damage-',
      progress.damagesCount,
      damages.length
    );
    progress.damagesCount = damages.length;
    for (let idx = 0; idx < damages.length; idx++) {
      const key = `damage-${idx}`;
      if (progress.completed[key]) {
        continue;
      }

      await this.sendFolderDamage(damages[idx], content);
      progress.completed[key] = true;
      onProgress?.();
    }

    const infoImages = folder.infoImages ?? [];
    this.resetSectionIfShapeChanged(
      progress,
      'info-',
      progress.infoImagesCount,
      infoImages.length
    );
    progress.infoImagesCount = infoImages.length;
    for (let idx = 0; idx < infoImages.length; idx++) {
      const key = `info-${idx}`;
      if (progress.completed[key]) {
        continue;
      }

      await this.sendFolderImage(infoImages[idx], 'info', content);
      progress.completed[key] = true;
      onProgress?.();
    }
  }

  // damagesImages/infoImages progress is keyed by array index. If the
  // array's length changed since the last attempt (an entry was added or
  // removed while going back through the wizard after a failed submit),
  // those indices no longer point at the same items — resending the whole
  // section is the only way to avoid silently skipping an item that was
  // never actually uploaded.
  private resetSectionIfShapeChanged(
    progress: SubmissionProgress,
    keyPrefix: string,
    previousCount: number | undefined,
    currentCount: number
  ) {
    if (previousCount === undefined || previousCount === currentCount) {
      return;
    }

    for (const key of Object.keys(progress.completed)) {
      if (key.startsWith(keyPrefix)) {
        delete progress.completed[key];
      }
    }
    for (const key of Object.keys(progress.mediaIds)) {
      if (key.startsWith(keyPrefix)) {
        delete progress.mediaIds[key];
      }
    }
  }

  public async sendFolderVehicleImage(
    images: CreateVehicleImage,
    wheels: WheelImage,
    folder: Folder,
    progress: SubmissionProgress,
    onProgress?: () => void
  ) {
    for (const [localKey, patchField] of Object.entries(
      VEHICLE_IMAGE_FIELDS
    )) {
      const image = (images as any)[localKey];
      if (!image) {
        continue;
      }

      await this.sendFolderFieldOnce(
        localKey,
        patchField,
        image,
        folder,
        progress,
        onProgress
      );
    }

    for (const [localKey, patchField] of Object.entries(WHEEL_FIELDS)) {
      const image = (wheels as any)[localKey];
      if (!image) {
        continue;
      }

      await this.sendFolderFieldOnce(
        localKey,
        patchField,
        image,
        folder,
        progress,
        onProgress
      );
    }
  }

  // Uploads (or reuses) the MediaObject for one Folder field, then PATCHes
  // it onto the Folder — each half is skipped if already done.
  private async sendFolderFieldOnce(
    progressKey: string,
    patchField: string,
    image: string,
    folder: Folder,
    progress: SubmissionProgress,
    onProgress?: () => void
  ) {
    if (progress.completed[progressKey]) {
      return;
    }

    const mediaId = await this.sendMediaOnce(
      progressKey,
      image,
      folder,
      progress,
      onProgress
    );

    await api(folder['@id'], {
      method: 'PATCH',
      body: JSON.stringify({
        [patchField]: mediaId,
      }),
    });

    progress.completed[progressKey] = true;
    onProgress?.();
  }

  private async sendFolderImageOnce(
    progressKey: string,
    img: string,
    type: string,
    folder: Folder,
    progress: SubmissionProgress,
    onProgress?: () => void
  ) {
    if (progress.completed[progressKey]) {
      return;
    }

    const mediaId = await this.sendMediaOnce(
      progressKey,
      img,
      folder,
      progress,
      onProgress
    );

    await api('/api/folder_images', {
      method: 'POST',
      body: JSON.stringify({
        media: mediaId,
        type,
        folder: folder['@id'],
      }),
    });

    progress.completed[progressKey] = true;
    onProgress?.();
  }

  private async sendMediaOnce(
    progressKey: string,
    image: string,
    folder: Folder,
    progress: SubmissionProgress,
    onProgress?: () => void
  ): Promise<string> {
    const existing = progress.mediaIds[progressKey];
    if (existing) {
      return existing;
    }

    const media = await this.sendMedia(image, folder);
    progress.mediaIds[progressKey] = media['@id'];
    onProgress?.();

    return media['@id'];
  }

  public async sendFolderImage(img: string, type: string, folder: Folder) {
    const media = await this.sendMedia(img, folder);
    return api('/api/folder_images', {
      method: 'POST',
      body: JSON.stringify({
        media: media['@id'],
        type,
        folder: folder['@id'],
      }),
    }).then((response) => response.json());
  }

  public async sendFolderDamage(damage: VehicleDamage, folder: Folder) {
    const media = await this.sendMedia(damage.img, folder);

    return api('/api/folder_damages', {
      method: 'POST',
      body: JSON.stringify({
        imagePath: media['@id'],
        type: damage.type,
        folder: folder['@id'],
      }),
    }).then((response) => response.json());
  }

  public async sendMedia(media: string, folder: Folder) {
    return api('/api/media_objects', {
      method: 'POST',
      body: await getFormData({
        file: media,
        folder: folder['@id'],
      }),
    }).then((response) => response.json());
  }
}

export default new FolderClient();
